
import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    title: 'Schedule Your Onboarding',
    description: 'Pick a time that works for you. Our team will walk you through everything in a quick 20-minute call.',
    highlight: true,
  },
  {
    number: '02',
    icon: 'ri-settings-3-line',
    title: 'We Build & Configure',
    description: 'Sit back while our experts set up your entire system — website, AI tools, automations, and more.',
    highlight: false,
  },
  {
    number: '03',
    icon: 'ri-rocket-2-line',
    title: 'Launch & Grow',
    description: 'Go live with confidence. We monitor performance and optimize to make sure you see real results.',
    highlight: false,
  },
];

export function OnboardingSteps() {
  const [visibleItems, setVisibleItems] = useState<boolean[]>([false, false, false]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          steps.forEach((_, i) => {
            setTimeout(() => {
              setVisibleItems((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 200);
          });
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="max-w-5xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
      <div className="text-center mb-14">
        <p className="text-primary text-xs font-bold tracking-widest uppercase mb-3">Your Journey Starts Here</p>
        <h2 className="font-display text-2xl lg:text-3xl font-bold text-secondary">Three Simple Steps to Get Started</h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {steps.map((step, i) => (
          <div
            key={step.number}
            className={`relative group transition-all duration-600 ${
              visibleItems[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div
              className={`relative rounded-2xl p-7 h-full transition-all duration-300 ${
                step.highlight
                  ? 'bg-white shadow-lg shadow-primary/5 border-2 border-primary/20 hover:border-primary/40'
                  : 'bg-white shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200'
              }`}
            >
              {step.highlight && (
                <div className="absolute -top-3 left-7 bg-primary text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1 rounded-full">
                  Next Step
                </div>
              )}

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                step.highlight ? 'bg-primary/10' : 'bg-gray-50'
              }`}>
                <i className={`${step.icon} text-xl ${step.highlight ? 'text-primary' : 'text-secondary'}`}></i>
              </div>

              <div className="text-[11px] font-bold text-gray-300 tracking-widest mb-2">STEP {step.number}</div>
              <h3 className="font-display text-lg font-bold text-secondary mb-2">{step.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
